import {
  Box,
  FormControlLabel,
  InputAdornment,
  Switch,
  TextField,
  Typography,
  useTheme,
  CircularProgress,
} from '@mui/material';
import { useDropzone } from 'react-dropzone';
import { useEffect, useState } from 'react';
import { useSnackbar } from 'notistack';
import { StyledBoxStyle } from './style';
import { useCustomDashboardContext } from 'src/contexts/customDashboardContext/customDashboard.context';
import { useAuthentication } from 'src/features/authentication/context';
import { ImageUploadScadaIcon } from 'src/assets/icons/SCADA/assetsComponents/general/ImageUploadScadaIcon';
import { CloseIcon } from 'src/assets/icons/CloseIcon';
import { DashboardService } from 'src/services/dashboard/dashboard.service';

export const ImageUploadDrawer = () => {
  const { selectedNode, updateNodeData, nodes } = useCustomDashboardContext();
  const {
    customerId: { value: customerId },
  } = useAuthentication();
  const { enqueueSnackbar } = useSnackbar();

  const [imageInfo, setImageInfo] = useState({
    name: '',
    previewUrl: '',
    width: '',
    height: '',
    objectKey: '',
    isLockRatio: false,
  });
  const [isUploading, setIsUploading] = useState(false);
  const theme = useTheme();

  const findNode = nodes.find((node) => node.id === selectedNode?.id);

  const measuredWidth = findNode?.measured?.width;
  const measuredHeight = findNode?.measured?.height;

  const onDrop = async (acceptedFiles: File[]) => {
    const maxFileSize = 20 * 1024 * 1024;
    const file = acceptedFiles[0];

    if (!file || !customerId) {
      enqueueSnackbar('Missing required information for upload', {
        variant: 'error',
      });
      return;
    }

    if (file.size > maxFileSize) {
      enqueueSnackbar('Selected image exceeds 20MB limit', {
        variant: 'error',
      });
      return;
    }

    setIsUploading(true);

    try {
      const uploadUrlResponse = await DashboardService.getUploadUrl({
        customerId,
        fileName: file.name,
        contentType: file.type,
      });

      if (!uploadUrlResponse.presignedUrl || !uploadUrlResponse.objectKey) {
        throw new Error('Invalid response from presigned URL endpoint');
      }

      await DashboardService.uploadFileToS3(uploadUrlResponse.presignedUrl, file, file.type);

      const previewUrl = URL.createObjectURL(file);
      setImageInfo((prevState) => {
        const updated = {
          ...prevState,
          name: file.name,
          previewUrl,
          objectKey: uploadUrlResponse.objectKey,
        };

        if (selectedNode?.id) {
          updateNodeData(selectedNode.id, updated);
        }

        return updated;
      });

      enqueueSnackbar('Image uploaded successfully', { variant: 'success' });
    } catch (error) {
      enqueueSnackbar(error instanceof Error ? error.message : 'Failed to upload image', {
        variant: 'error',
      });
    } finally {
      setIsUploading(false);
    }
  };

  const { getRootProps, getInputProps } = useDropzone({
    accept: { 'image/*': [] },
    multiple: false,
    onDrop,
    disabled: isUploading,
  });

  const removeAddedPhotoHandler = () => {
    const updated = { name: '', previewUrl: '', objectKey: '', url: '' };
    setImageInfo((prevState) => ({ ...prevState, ...updated }));

    if (selectedNode?.id) {
      updateNodeData(selectedNode.id, updated);
    }
  };

  useEffect(() => {
    if (!selectedNode?.id) return;

    const findNode = nodes.find((node) => node.id === selectedNode.id);
    const nodeData = findNode?.data as {
      name?: string;
      previewUrl?: string;
      url?: string;
      width?: string;
      height?: string;
      isLockRatio?: boolean;
      objectKey?: string;
    };

    const previewUrl = nodeData?.previewUrl ?? nodeData?.url ?? '';

    let name = nodeData?.name ?? '';
    if (!name && previewUrl) {
      try {
        const urlPath = new URL(previewUrl).pathname;
        const fileName = urlPath.split('/').pop() ?? '';
        name = decodeURIComponent(fileName);
      } catch {
        name = '';
      }
    }

    const width = nodeData?.width ?? '';
    const height = nodeData?.height ?? '';
    const isLockRatio = nodeData?.isLockRatio ?? false;
    const objectKey = nodeData?.objectKey ?? '';

    setImageInfo({ name, previewUrl, width, height, isLockRatio, objectKey });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedNode?.id]);

  const onChangeHandler = (name: string, value: string | boolean | null) => {
    setImageInfo((prevState) => {
      const updated = { ...prevState, [name]: value };

      if (selectedNode?.id) {
        updateNodeData(selectedNode.id, updated);
      }

      return updated;
    });
  };

  useEffect(() => {
    if (measuredWidth) {
      setImageInfo((prev) => ({
        ...prev,
        width: String(measuredWidth),
      }));
    }
  }, [measuredWidth]);

  useEffect(() => {
    if (measuredHeight) {
      setImageInfo((prev) => ({ ...prev, height: String(measuredHeight) }));
    }
  }, [measuredHeight]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
      {imageInfo?.name ? (
        <StyledBoxStyle>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '1rem',
              width: '100%',
            }}
          >
            <Typography
              sx={{
                wordBreak: 'break-word',
                overflowWrap: 'break-word',
                maxWidth: '70%',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
              }}
            >
              {imageInfo.name}
            </Typography>
            <Box
              onClick={removeAddedPhotoHandler}
              sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
            >
              <CloseIcon width={10} />
            </Box>
          </Box>
        </StyledBoxStyle>
      ) : (
        <StyledBoxStyle
          {...getRootProps()}
          sx={{
            opacity: isUploading ? 0.6 : 1,
            pointerEvents: isUploading ? 'none' : 'auto',
          }}
        >
          <input {...getInputProps()} />
          {isUploading ? (
            <CircularProgress size={40} />
          ) : (
            <>
              <ImageUploadScadaIcon />
              <Typography
                sx={{
                  color: theme.palette.grey[500],
                  fontWeight: 700,
                  fontSize: '0.9rem',
                }}
              >
                Upload image
              </Typography>
              <Typography sx={{ color: theme.palette.grey[500], fontSize: '0.9rem' }}>
                Supported formats: SVG, PNG, JPG
              </Typography>
            </>
          )}
        </StyledBoxStyle>
      )}
      {imageInfo.previewUrl && (
        <Box sx={{ display: 'flex', gap: '16px' }}>
          <TextField
            size="small"
            value={imageInfo.width}
            label="Width"
            name="width"
            onChange={(e) => onChangeHandler('width', e.target.value)}
            InputProps={{
              endAdornment: <InputAdornment position="end">px</InputAdornment>,
            }}
          />

          <TextField
            size="small"
            value={imageInfo.height}
            label="Height"
            name="height"
            onChange={(e) => onChangeHandler('height', e.target.value)}
            InputProps={{
              endAdornment: <InputAdornment position="end">px</InputAdornment>,
            }}
          />
          <FormControlLabel
            control={
              <Switch
                checked={imageInfo.isLockRatio}
                name="isLockRatio"
                onChange={(e) => onChangeHandler(e.target.name, e.target.checked)}
              />
            }
            label={
              <Typography fontSize="12px" noWrap>
                Lock ratio
              </Typography>
            }
          />
        </Box>
      )}
    </Box>
  );
};
